import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  IconButton,
  SimpleGrid,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { MdArrowBack, MdEdit, MdLock } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AppCard,
  OutlineButton,
  PageLoader,
  PrimaryButton,
  StatusBadge,
} from '../../../components/ui';
import { findBuById } from '../../../data/master/businessUnits';
import {
  mergeRolePermissions,
  permissionSummaryLines,
  STAFF_SCOPE_TYPES,
} from '../../../data/staff/permissions';
import { setRolesCache } from '../../../data/staff/roles';
import { lookupBusinessUnits, lookupSites } from '../../../repositories/lookup.repository';
import { listRoles } from '../../../repositories/role.repository';
import { getStaff } from '../../../repositories/staff.repository';
import { formatStaffScope } from '../../../utils/staffScope';
import { StaffActivityTable } from './StaffActivityTable';
import { StaffFormModal } from './StaffFormModal';
import { StaffPasswordModal } from './StaffPasswordModal';

function DetailRow({ label, value }) {
  return (
    <Box
      p={{ base: 3, xl: 4 }}
      bg="bg.input"
      borderRadius="sm"
      borderWidth="1px"
      borderColor="border.subtle"
    >
      <Text fontSize="12px" color="text.secondary" mb={1} fontWeight="500">
        {label}
      </Text>
      <Text fontSize="15px" fontWeight="600" whiteSpace="pre-wrap">
        {value || '—'}
      </Text>
    </Box>
  );
}

export default function StaffDetailPage() {
  const { staffId: routeId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const formModal = useDisclosure();
  const passwordModal = useDisclosure();
  const [staff, setStaff] = useState(null);
  const [roles, setRoles] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activityKey, setActivityKey] = useState(0);

  const loadData = useCallback(async () => {
    if (!routeId) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setNotFound(false);
    try {
      const [staffRow, rolesResult, buResult, sitesResult] = await Promise.all([
        getStaff(routeId),
        listRoles({ page: 1, pageSize: 200 }),
        lookupBusinessUnits(),
        lookupSites(),
      ]);
      if (!staffRow) {
        setStaff(null);
        setNotFound(true);
        return;
      }
      const roleItems = rolesResult.items || [];
      setStaff(staffRow);
      setRoles(roleItems);
      setRolesCache(roleItems);
      setBusinessUnits(buResult || []);
      setSites(sitesResult || []);
    } catch (err) {
      setStaff(null);
      setNotFound(true);
      toast({
        title: err?.message || 'Failed to load staff user',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  }, [routeId, toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const assignedRoles = useMemo(
    () =>
      (staff?.roleIds || [])
        .map((id) => roles.find((r) => r.roleId === id))
        .filter(Boolean),
    [staff, roles],
  );

  const rightsPreview = useMemo(() => {
    if (!assignedRoles.length) return [];
    return permissionSummaryLines(mergeRolePermissions(assignedRoles));
  }, [assignedRoles]);

  if (loading) {
    return (
      <Box>
        <OutlineButton
          size="sm"
          leftIcon={<MdArrowBack />}
          mb={4}
          onClick={() => navigate('/admin/administration/staff')}
        >
          Back to staff
        </OutlineButton>
        <AppCard>
          <PageLoader label="Loading staff user…" minH="160px" py={8} />
        </AppCard>
      </Box>
    );
  }

  if (notFound || !staff) {
    return (
      <Box>
        <OutlineButton
          size="sm"
          leftIcon={<MdArrowBack />}
          mb={4}
          onClick={() => navigate('/admin/administration/staff')}
        >
          Back to staff
        </OutlineButton>
        <AppCard>
          <Text color="text.secondary" py={8} textAlign="center">
            Staff user not found — {routeId}
          </Text>
        </AppCard>
      </Box>
    );
  }

  const bu = findBuById(businessUnits, staff.buId);
  const scopeLabel = formatStaffScope(staff, businessUnits, sites);
  const scopeTypeLabel =
    STAFF_SCOPE_TYPES.find((t) => t.value === staff.scopeType)?.label || staff.scopeType;

  return (
    <Box>
      <Flex
        justify="space-between"
        align={{ base: 'start', md: 'center' }}
        gap={4}
        mb={6}
        wrap="wrap"
      >
        <Flex align="center" gap={3}>
          <IconButton
            aria-label="Back"
            icon={<MdArrowBack />}
            variant="ghostInk"
            size="sm"
            onClick={() => navigate('/admin/administration/staff')}
          />
          <Box>
            <Heading size={{ base: 'lg', xl: 'xl' }}>{staff.fullName}</Heading>
            <Text color="text.secondary" mt={1} fontFamily="mono" fontSize="14px">
              {staff.staffId}
            </Text>
          </Box>
        </Flex>
        <Flex gap={2} wrap="wrap">
          <OutlineButton
            size="sm"
            leftIcon={<MdLock />}
            onClick={passwordModal.onOpen}
          >
            Update password
          </OutlineButton>
          <PrimaryButton size="sm" leftIcon={<MdEdit />} onClick={formModal.onOpen}>
            Edit staff
          </PrimaryButton>
        </Flex>
      </Flex>

      <AppCard mb={4}>
        <Heading size="sm" mb={4}>
          Profile
        </Heading>
        <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={3}>
          <DetailRow label="Email" value={staff.email} />
          <DetailRow label="Mobile" value={staff.mobile} />
          <DetailRow
            label="Status"
            value={staff.active !== false ? 'Active' : 'Inactive'}
          />
          <DetailRow
            label="Last login"
            value={
              staff.lastLoginAt
                ? String(staff.lastLoginAt).slice(0, 16).replace('T', ' ')
                : 'Never'
            }
          />
          <DetailRow label="Data scope" value={scopeLabel} />
          <DetailRow label="Scope type" value={scopeTypeLabel} />
          <DetailRow label="Business Unit" value={bu?.shortName || '—'} />
          <DetailRow
            label="Password"
            value={staff.passwordSet !== false ? 'Set' : 'Not set'}
          />
        </SimpleGrid>
      </AppCard>

      <AppCard mb={4}>
        <Heading size="sm" mb={3}>
          Roles
        </Heading>
        <Flex gap={2} flexWrap="wrap" mb={4}>
          {assignedRoles.map((role) => (
            <StatusBadge key={role.roleId} status="info">
              {role.name}
            </StatusBadge>
          ))}
        </Flex>
        <Box
          p={4}
          bg="bg.input"
          borderRadius="sm"
          borderWidth="1px"
          borderColor="border.subtle"
        >
          <Text fontSize="13px" fontWeight="600" mb={2}>
            Combined rights
          </Text>
          {rightsPreview.length ? (
            <VStack align="stretch" spacing={1}>
              {rightsPreview.map((line) => (
                <Text key={line} fontSize="13px" color="text.secondary">
                  • {line}
                </Text>
              ))}
            </VStack>
          ) : (
            <Text fontSize="13px" color="text.secondary">
              No rights assigned.
            </Text>
          )}
        </Box>
      </AppCard>

      <AppCard p={0} overflow="hidden">
        <Box px={{ base: 5, xl: 6 }} pt={{ base: 5, xl: 6 }} pb={3}>
          <Box>
            <Heading size="sm">Activity</Heading>
            <Text fontSize="13px" color="text.secondary" mt={1}>
              Recent actions by this user — KYC, edits, logins, and more.
            </Text>
          </Box>
        </Box>
        <StaffActivityTable
          key={activityKey}
          staffId={staff.id}
          emptyMessage="No activity recorded for this user yet."
        />
      </AppCard>

      <StaffFormModal
        isOpen={formModal.isOpen}
        onClose={formModal.onClose}
        staff={staff}
        roles={roles}
        businessUnits={businessUnits}
        sites={sites}
        onSaved={() => {
          loadData();
          setActivityKey((k) => k + 1);
        }}
      />
      <StaffPasswordModal
        isOpen={passwordModal.isOpen}
        onClose={passwordModal.onClose}
        staff={staff}
        onSaved={() => {
          loadData();
          setActivityKey((k) => k + 1);
        }}
      />
    </Box>
  );
}
